import React from "react";
import { useNavigate } from "react-router-dom";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import { Application, useCreateTestRdcAgentMutation } from "../../graphql/generated";
import { MlsContext } from "../../mls/context/mls";
import { useTitle } from "../../common/utils/title";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

const AgentRdcNew: React.FC = () => {
  useTitle("New RDC Test Agent", "Agents");
  const navigate = useNavigate();

  const { mlses } = React.useContext(MlsContext);
  const { notifier } = React.useContext(NotificationContext);

  const [mlsState, setMlsState] = React.useState("");
  const [firstNameState, setFirstNameState] = React.useState("");
  const [lastNameState, setLastNameState] = React.useState("");
  const [mlsIdState, setMlsIdState] = React.useState("");
  const [emailState, setEmailState] = React.useState("");
  const [createdAgent, setCreatedAgent] = React.useState<{
    id: string;
    password: string;
  } | null>(null);

  const [{ error, data, fetching }, mutation] = useCreateTestRdcAgentMutation();

  // Filter to only RDC+ MLSes (application: "RealtorPlus")
  const rdcMlses = React.useMemo(
    () => mlses.filter((mls) => mls.application === Application.RealtorPlus),
    [mlses]
  );

  const submitForm = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      mutation({
        details: {
          firstName: firstNameState,
          lastName: lastNameState,
          memberId: mlsIdState,
          mls: mlsState,
          email: emailState,
        },
      });
    },
    [emailState, firstNameState, lastNameState, mlsIdState, mlsState, mutation]
  );

  React.useEffect(() => {
    if (!error && data && !fetching) {
      const agent = data.createTestRdcAgent?.agent;
      const password = data.createTestRdcAgent?.password;
      if (agent && password) {
        setCreatedAgent({
          id: agent.id,
          password,
        });
      }
    } else if (error) {
      notifier.error(error);
    }
  }, [data, error, fetching]);

  if (createdAgent) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                <CheckCircleIcon
                  className="h-6 w-6 text-green-600"
                  aria-hidden="true"
                />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  RDC Test Agent Created Successfully
                </h3>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Agent URL
                    </label>
                    <div className="mt-1">
                      <a
                        href={`https://admin.zenlist.team/agents/${createdAgent.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-900 break-all"
                      >
                        https://admin.zenlist.team/agents/{createdAgent.id}
                      </a>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <div className="mt-1 font-mono text-lg bg-gray-100 p-3 rounded border border-gray-300">
                      {emailState}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="mt-1 font-mono text-lg bg-gray-100 p-3 rounded border border-gray-300">
                      {createdAgent.password}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Save this password - it won't be shown again
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Login URL
                    </label>
                    <div className="mt-1">
                      <a
                        href="https://www.realtor.com/plus/app/enterprise?e=rdcplus-mls-test"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        https://www.realtor.com/plus/app/enterprise?e=rdcplus-mls-test
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => navigate("/agents")}
              >
                Return to Agents
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={() => {
                  setCreatedAgent(null);
                  setMlsState("");
                  setFirstNameState("");
                  setLastNameState("");
                  setMlsIdState("");
                  setEmailState("");
                }}
              >
                Create Another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              New Test RDC Agent
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Creates a test agent and Cognito user in the RDC+ MLS Test pool
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:mx-4 sm:rounded-md">
        <div className="p-4">
          <form
            className="space-y-8 divide-y divide-gray-200"
            onSubmit={submitForm}
          >
            <div className="space-y-8 divide-y divide-gray-200 sm:space-y-5">
              <div>
                <div className="space-y-6 sm:space-y-5">
                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
                    <label
                      htmlFor="mls"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      MLS (RDC+ Only)
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <select
                        required
                        id="mls"
                        name="mls"
                        value={mlsState}
                        onChange={(e) => setMlsState(e.target.value)}
                        className="max-w-lg block focus:ring-indigo-500 focus:border-indigo-500 w-full shadow-sm sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      >
                        <option value={""}>Please Select</option>
                        {rdcMlses.map((mls) => (
                          <option key={mls.id} value={mls.id}>
                            {mls.name} - {mls.shortName}
                          </option>
                        ))}
                      </select>
                      {rdcMlses.length === 0 && (
                        <p className="mt-2 text-sm text-red-600">
                          No RDC+ MLSes available
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      First Name
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="text"
                        name="first-name"
                        id="first-name"
                        value={firstNameState}
                        onChange={(e) => setFirstNameState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="last-name"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      Last Name
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="text"
                        name="last-name"
                        id="last-name"
                        value={lastNameState}
                        onChange={(e) => setLastNameState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="member-id"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      MLS Member ID
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="text"
                        name="member-id"
                        id="member-id"
                        value={mlsIdState}
                        onChange={(e) => setMlsIdState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                      <p className="mt-2 text-sm text-gray-500">
                        Will be prefixed with "ZL:" automatically
                      </p>
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                    >
                      Email
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                      <input
                        required
                        type="email"
                        name="email"
                        id="email"
                        value={emailState}
                        onChange={(e) => setEmailState(e.target.value)}
                        className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  disabled={fetching}
                  type="submit"
                  className={classNames(
                    fetching
                      ? "text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      : "text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500",
                    "ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md"
                  )}
                >
                  {fetching ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AgentRdcNew;
